import { AuthErrorCode } from '@today/types'

/**
 * Machine-readable login failure codes (openspec `adapt-services-events`,
 * REQ-006 / SCN-008).
 *
 * Surfaced as the `reason` on the navigation state when the callback routes to
 * `/error/unauthorized`, so a failure is identifiable rather than an anonymous
 * "sign-in failed" screen.
 */

/**
 * The authorization code could not be exchanged for tokens — no matching stashed
 * PKCE verifier (different browser/tab, cleared storage, or a replayed callback
 * link), or the token endpoint rejected the exchange. No tokens are stored.
 */
export const LOGIN_CODE_EXCHANGE_FAILED = 'LOGIN_CODE_EXCHANGE_FAILED'

/**
 * The tenant allows just-in-time provisioning, but the verified email's domain
 * is not on its allowlist, so no account was created (TO-335 REQ-005).
 */
export const JIT_DOMAIN_NOT_ALLOWED = 'JIT_DOMAIN_NOT_ALLOWED'

/**
 * The verified email already belongs to a user in that tenant. JIT never links
 * an existing account — an admin does (TO-335 REQ-008).
 */
export const JIT_EMAIL_CONFLICT = 'JIT_EMAIL_CONFLICT'

/** Every failure code this app defines. Anything else is not ours to display. */
const KNOWN_LOGIN_FAILURE_REASONS: ReadonlySet<string> = new Set([
	LOGIN_CODE_EXCHANGE_FAILED,
	JIT_DOMAIN_NOT_ALLOWED,
	JIT_EMAIL_CONFLICT,
])

/** The BFF's machine-readable `error.code`, if the response carries one. */
const errorCode = (error: unknown): unknown =>
	(error as { response?: { data?: { error?: { code?: unknown } } } } | null)?.response?.data?.error?.code

/**
 * The refusal code the BFF returned, when this app knows how to explain it.
 * Anything else — including an unknown JIT-era code — falls back to the generic
 * exchange failure, so a server-supplied string is never rendered.
 */
export function loginFailureFromApiError(error: unknown): string {
	const code = errorCode(error)
	return typeof code === 'string' && KNOWN_LOGIN_FAILURE_REASONS.has(code) ? code : LOGIN_CODE_EXCHANGE_FAILED
}

/**
 * Narrow an untrusted `location.state.reason` to a code we actually define.
 *
 * The error page renders this value, so it must never be free text: a future
 * call site (or a same-origin bug) passing a caller-controlled string would turn
 * the page into a content-injection / phishing surface. React escapes the text,
 * so this is about *what it says*, not markup.
 */
export function asKnownLoginFailureReason(reason: unknown): string | null {
	return typeof reason === 'string' && KNOWN_LOGIN_FAILURE_REASONS.has(reason) ? reason : null
}

/**
 * The signed-in Cognito subject maps to no TODAY account — the BFF refuses the
 * callback with 403 (TO-361 login-surface REQ-005).
 */
export const USER_NOT_PROVISIONED = 'USER_NOT_PROVISIONED'

/**
 * The subject maps to an inactive or ambiguous account, or its tenant is
 * suspended — the BFF refuses the callback with 403 (TO-361 login-surface REQ-005).
 */
export const PRINCIPAL_INACTIVE = 'PRINCIPAL_INACTIVE'

/** The generic `/error/no-access` message key (`<namespace>.<key path>`). */
export const NO_ACCESS_DEFAULT_MESSAGE_KEY = 'auth.noAccess.message'

/**
 * The MFA refusals at sign-in (TO-63 privileged-mfa REQ-014) and the
 * code-specific message each shows on `/error/no-access`, instead of the
 * generic one.
 */
const MFA_REFUSAL_MESSAGE_KEYS: ReadonlyMap<string, string> = new Map([
	[AuthErrorCode.MFA_REQUIRED, 'auth.errors.mfaRequired'],
	[AuthErrorCode.IDP_MFA_REQUIRED, 'auth.errors.idpMfaRequired'],
])

/** The refusals that land on `/error/no-access` instead of `/error/unauthorized`. */
const NO_ACCESS_REASONS: ReadonlySet<string> = new Set([
	USER_NOT_PROVISIONED,
	PRINCIPAL_INACTIVE,
	...MFA_REFUSAL_MESSAGE_KEYS.keys(),
])

/** The sign-in failure screens the callback routes to. */
export const UNAUTHORIZED_PATH = '/error/unauthorized'
export const NO_ACCESS_PATH = '/error/no-access'

/** Where a failed sign-in goes, and the reason it carries on the navigation state. */
export interface LoginFailureRoute {
	path: string
	reason: string
	/** A code-specific message key (`<namespace>.<key path>`), when the refusal has one. */
	messageKey?: string
}

/** The message key `/error/no-access` shows for an (already narrowed) reason. */
export function noAccessMessageKey(reason: string | null): string {
	return (reason !== null && MFA_REFUSAL_MESSAGE_KEYS.get(reason)) || NO_ACCESS_DEFAULT_MESSAGE_KEY
}

/**
 * Routes a failed callback exchange (TO-361 login-surface REQ-005): an account
 * refusal goes to `/error/no-access` with its code — and, for an MFA refusal
 * (TO-63 REQ-014), its specific message key; everything else keeps the
 * `/error/unauthorized` routing with {@link loginFailureFromApiError}'s reason.
 */
export function loginFailureRoute(error: unknown): LoginFailureRoute {
	const code = errorCode(error)
	if (typeof code === 'string' && NO_ACCESS_REASONS.has(code)) {
		const messageKey = MFA_REFUSAL_MESSAGE_KEYS.get(code)
		return messageKey ? { path: NO_ACCESS_PATH, reason: code, messageKey } : { path: NO_ACCESS_PATH, reason: code }
	}
	return { path: UNAUTHORIZED_PATH, reason: loginFailureFromApiError(error) }
}

/** Narrow an untrusted `/error/no-access` `state.reason` to one of the no-access refusal codes. */
export function asKnownNoAccessReason(reason: unknown): string | null {
	return typeof reason === 'string' && NO_ACCESS_REASONS.has(reason) ? reason : null
}
